import type {
  VesselSearcherBooleanFilter,
  VesselSearcherEditableNumberRange,
  VesselSearcherFilterOptions,
  VesselSearcherFilters,
  VesselSearcherOptionField,
  VesselSearcherRangeFilterKey,
  VesselSearcherRequest,
} from "../models/vessel-searcher.models";
import { getMockVesselSearcherOptions } from "../mocks/vessel-searcher.mocks";
import { getVesselSearcherOptions } from "../services/vessel-searcher.service";
import { FunnelIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useEffect, useRef, useState } from "react";

interface VesselSearcherFiltersProps {
  filters: VesselSearcherFilters;
  options: VesselSearcherFilterOptions;
  useMockData: boolean;
  activeRangeFilters: VesselSearcherRangeFilterKey[];
  onChange: (filters: VesselSearcherFilters) => void;
  onClear: () => void;
}

const fieldClass = "h-9 w-full rounded-lg border border-gray-300 bg-white px-3 text-xs text-gray-700 outline-none focus:border-green focus:ring-1 focus:ring-green dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200";

const FilterLabel = ({ children }: { children: string }) => (
  <span className="mb-1.5 block text-xs font-medium text-gray-600 dark:text-gray-300">{children}</span>
);

const formatSelectOption = (option: string): string => {
  if (option === "yes") {
    return "Yes";
  }

  if (option === "no") {
    return "No";
  }

  if (option === "blank") {
    return "(Blank)";
  }

  return option;
};

const SelectFilter = ({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
}) => (
  <label className="block">
    <FilterLabel>{label}</FilterLabel>
    <select value={value} onChange={(event) => onChange(event.target.value)} className={fieldClass}>
      <option value="all">All</option>
      {options.map((option) => (
        <option key={option} value={option}>{formatSelectOption(option)}</option>
      ))}
    </select>
  </label>
);

const MultiSelectFilter = ({
  label,
  values,
  options,
  onChange,
}: {
  label: string;
  values: string[];
  options: string[];
  onChange: (values: string[]) => void;
}) => (
  <div>
    <FilterLabel>{label}</FilterLabel>
    <details className="group relative">
      <summary className={`${fieldClass} flex cursor-pointer list-none items-center justify-between`}>
        <span className="truncate">{values.length === 0 ? "All" : `${values.length} selected`}</span>
        <span aria-hidden="true" className="text-[10px] group-open:rotate-180">v</span>
      </summary>
      <div className="absolute z-30 mt-1 max-h-56 w-full overflow-y-auto rounded-lg border border-gray-200 bg-white p-2 shadow-lg dark:border-gray-600 dark:bg-gray-700">
        {values.length > 0 && (
          <button type="button" onClick={() => onChange([])} className="mb-1 w-full rounded px-2 py-1 text-left text-xs font-semibold text-green hover:bg-gray-50 dark:hover:bg-gray-600">
            Clear selection
          </button>
        )}
        {options.map((option) => (
          <label key={option} className="flex cursor-pointer items-start gap-2 rounded px-2 py-1 text-xs hover:bg-gray-50 dark:hover:bg-gray-600">
            <input
              type="checkbox"
              checked={values.includes(option)}
              onChange={() => onChange(values.includes(option) ? values.filter((value) => value !== option) : [...values, option])}
              className="mt-0.5 accent-green"
            />
            <span>{option}</span>
          </label>
        ))}
      </div>
    </details>
  </div>
);

const SearchableOptionFilter = ({
  label,
  field,
  value,
  filters,
  activeRangeFilters,
  useMockData,
  onChange,
}: {
  label: string;
  field: VesselSearcherOptionField;
  value: string;
  filters: VesselSearcherFilters;
  activeRangeFilters: VesselSearcherRangeFilterKey[];
  useMockData: boolean;
  onChange: (value: string) => void;
}) => {
  const [search, setSearch] = useState(value);
  const [options, setOptions] = useState<string[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const pendingTypedImoFilterValue = useRef<string | null>(null);
  const listboxId = `vessel-searcher-${field}-options`;

  useEffect(() => {
    if (pendingTypedImoFilterValue.current === value) {
      pendingTypedImoFilterValue.current = null;

      return;
    }

    setSearch(value);
  }, [value]);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    const timer = window.setTimeout(() => {
      const optionRequest: VesselSearcherRequest = {
        ...filters,
        activeRangeFilters,
        sortBy: "dwt",
        sortDirection: "desc",
        offset: 0,
        limit: 50,
      };
      const request = useMockData
        ? Promise.resolve(getMockVesselSearcherOptions(field, search, optionRequest))
        : getVesselSearcherOptions(field, search, { ...filters, activeRangeFilters }, controller.signal);

      request.then((nextOptions) => {
        if (active) setOptions(nextOptions);
      }).catch(() => {
        if (active && !controller.signal.aborted) setOptions([]);
      });
    }, 250);

    return () => {
      window.clearTimeout(timer);
      active = false;
      controller.abort();
    };
  }, [activeRangeFilters, field, filters, search, useMockData]);

  // Commits a complete typed IMO directly to the dashboard filter.
  // Partial IMO text remains local to autocomplete so the table endpoint never receives an invalid identifier.
  const updateSearchTextAndCommitCompleteImo = (nextSearch: string) => {
    setSearch(nextSearch);

    if (field === "imo") {
      const normalizedImo = nextSearch.trim();
      const nextValue = /^\d{7}$/.test(normalizedImo) ? normalizedImo : "";

      if (nextValue !== value) {
        pendingTypedImoFilterValue.current = nextValue;
        onChange(nextValue);
      }

      return;
    }

    if (value) {
      onChange("");
    }
  };

  return (
    <label className="relative block">
      <FilterLabel>{label}</FilterLabel>
      <input
        value={search}
        onFocus={() => setIsOpen(true)}
        onBlur={() => setIsOpen(false)}
        onChange={(event) => updateSearchTextAndCommitCompleteImo(event.target.value)}
        inputMode={field === "imo" ? "numeric" : undefined}
        maxLength={field === "imo" ? 7 : undefined}
        placeholder={`All ${label.toLowerCase()}s`}
        className={fieldClass}
        role="combobox"
        aria-controls={listboxId}
        aria-expanded={isOpen}
        aria-autocomplete="list"
        data-test={`vessel-searcher-${field}-search-input`}
      />
      {isOpen && (
        <div id={listboxId} role="listbox" className="absolute z-30 mt-1 max-h-56 w-full overflow-y-auto rounded-lg border border-gray-200 bg-white p-1 shadow-lg dark:border-gray-600 dark:bg-gray-700">
          {options.length === 0 ? (
            <div className="px-2 py-1.5 text-xs text-gray-500">No matches</div>
          ) : options.map((option) => (
            <button
              key={option}
              type="button"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => {
                setSearch(option);
                onChange(option);
                setIsOpen(false);
              }}
              className="block w-full rounded px-2 py-1.5 text-left text-xs hover:bg-gray-50 dark:hover:bg-gray-600"
            >
              {option}
            </button>
          ))}
        </div>
      )}
    </label>
  );
};

const NumberRangeFilter = ({
  label,
  values,
  onChange,
}: {
  label: string;
  values: VesselSearcherEditableNumberRange;
  onChange: (values: VesselSearcherEditableNumberRange) => void;
}) => {
  const parseValue = (value: string): number | "" => value === "" ? "" : Number(value);

  return (
    <fieldset>
      <legend><FilterLabel>{label}</FilterLabel></legend>
      <div className="grid grid-cols-2 gap-2">
        <input
          aria-label={`${label} minimum`}
          type="number"
          min="0"
          value={values.min}
          onChange={(event) => onChange({ ...values, min: parseValue(event.target.value) })}
          className={fieldClass}
        />
        <input
          aria-label={`${label} maximum`}
          type="number"
          min="0"
          value={values.max}
          onChange={(event) => onChange({ ...values, max: parseValue(event.target.value) })}
          className={fieldClass}
        />
      </div>
    </fieldset>
  );
};

// Presents every PBIP slicer in the original order while keeping all filter state in the page-owned hook.
const VesselSearcherFiltersPanel = ({ filters, options, useMockData, activeRangeFilters, onChange, onClear }: VesselSearcherFiltersProps) => {
  const patch = <Key extends keyof VesselSearcherFilters>(key: Key, value: VesselSearcherFilters[Key]) =>
    onChange({ ...filters, [key]: value });

  return (
    <aside className="flex min-h-0 flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800" data-test="vessel-searcher-filters">
      <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3 dark:border-gray-700">
        <div className="flex items-center gap-2"><FunnelIcon className="h-5 w-5 text-green" /><h2 className="text-sm font-semibold">Filters</h2></div>
        <button type="button" onClick={onClear} className="inline-flex items-center gap-1 text-[11px] font-semibold text-green hover:underline" data-test="vessel-searcher-clear-filters">
          <XMarkIcon className="h-3.5 w-3.5" />Clear
        </button>
      </div>
      <div className="min-h-0 flex-1 space-y-3.5 overflow-y-auto p-4">
        <SearchableOptionFilter label="IMO" field="imo" value={filters.imo} filters={filters} activeRangeFilters={activeRangeFilters} useMockData={useMockData} onChange={(value) => patch("imo", value)} />
        <SearchableOptionFilter label="Vessel name" field="vesselName" value={filters.vesselName} filters={filters} activeRangeFilters={activeRangeFilters} useMockData={useMockData} onChange={(value) => patch("vesselName", value)} />
        <SelectFilter label="Use scrubber" value={filters.useScrubber} options={["yes", "no", "blank"]} onChange={(value) => patch("useScrubber", value as VesselSearcherBooleanFilter)} />
        <SelectFilter label="Has Fixture in IMOS" value={filters.hasVoyageInImos} options={["yes", "no"]} onChange={(value) => patch("hasVoyageInImos", value as VesselSearcherBooleanFilter)} />
        <fieldset><legend><FilterLabel>Build date</FilterLabel></legend><div className="grid grid-cols-2 gap-2">
          <input aria-label="Build date from" type="month" value={filters.buildDateFrom} onChange={(event) => patch("buildDateFrom", event.target.value)} className={fieldClass} />
          <input aria-label="Build date to" type="month" value={filters.buildDateTo} onChange={(event) => patch("buildDateTo", event.target.value)} className={fieldClass} />
        </div></fieldset>
        <NumberRangeFilter label="DWT" values={filters.dwt} onChange={(value) => patch("dwt", value)} />
        <NumberRangeFilter label="LBP" values={filters.lbp} onChange={(value) => patch("lbp", value)} />
        <NumberRangeFilter label="Draft" values={filters.draft} onChange={(value) => patch("draft", value)} />
        <NumberRangeFilter label="Depth" values={filters.depth} onChange={(value) => patch("depth", value)} />
        <NumberRangeFilter label="Breadth" values={filters.breadth} onChange={(value) => patch("breadth", value)} />
        <MultiSelectFilter label="Engine type" values={filters.engineTypes} options={options.engineTypes} onChange={(value) => patch("engineTypes", value)} />
        <MultiSelectFilter label="Design" values={filters.designs} options={options.designs} onChange={(value) => patch("designs", value)} />
        <MultiSelectFilter label="Yard" values={filters.yards} options={options.yards} onChange={(value) => patch("yards", value)} />
        <MultiSelectFilter label="Country" values={filters.countries} options={options.countries} onChange={(value) => patch("countries", value)} />
      </div>
    </aside>
  );
};

export default VesselSearcherFiltersPanel;
